import { resolveParserFlags } from './flags';
import { normalizeQuery } from './normalize';
import { runFilterExtractors } from './extractors';
import { extractLocation } from './extractors/location';
import { extractGooglePlacesLocation } from './extractors/google-places';
import { applyConfidenceStep, overallConfidence, stripConfidence } from './confidence';
import { deriveResidualFromSpans } from './residual';
import { matchCoverage } from './spans';
import { debugParserInput, debugParserOutput, emitMetrics } from './metrics';
import {
  createDefaultSmartSearchAiProvider,
  inferFiltersWithLlm,
  mergeFiltersFillGaps,
} from './llm-fallback';
import { ParsedSearchFiltersSchema, ParseNaturalLanguageFiltersResultSchema } from './schema';
import type {
  MatchSpan,
  ParseMetrics,
  ParseNaturalLanguageFiltersOptions,
  ParseNaturalLanguageFiltersResult,
  ParsedSearchFilters,
} from './types';

function matchedFieldNames(filters: ParsedSearchFilters): string[] {
  const fields: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    fields.push(key);
  }
  return fields;
}

function buildMetrics(args: {
  query: string;
  normalizedQuery: string;
  spans: ReadonlyArray<MatchSpan>;
  residual: string | null;
  filters: ParsedSearchFilters;
  durationMs: number;
  confidenceEnabled: boolean;
  flags: ReturnType<typeof resolveParserFlags>;
}): ParseMetrics {
  const { query, normalizedQuery, spans, residual, filters, durationMs, confidenceEnabled, flags } =
    args;
  const length = normalizedQuery.length || 1;
  const residualLen = residual?.length ?? 0;

  return {
    query,
    normalizedQuery,
    matchCoverage: matchCoverage(spans, normalizedQuery.length),
    residualRate: residualLen / length,
    durationMs,
    confidence: confidenceEnabled ? overallConfidence(spans) : null,
    matchedFields: matchedFieldNames(filters),
    residual,
    flags,
  };
}

function emitEmptyAndReturnNull(args: {
  trimmed: string;
  normalizedQuery: string;
  started: number;
  flags: ReturnType<typeof resolveParserFlags>;
  onMetrics: ParseNaturalLanguageFiltersOptions['onMetrics'];
}): null {
  const durationMs = performance.now() - args.started;
  emitMetrics(
    buildMetrics({
      query: args.trimmed,
      normalizedQuery: args.normalizedQuery,
      spans: [],
      residual: null,
      filters: {},
      durationMs,
      confidenceEnabled: args.flags.confidence,
      flags: args.flags,
    }),
    args.onMetrics
  );
  debugParserOutput(null);
  return null;
}

/**
 * Natural-language → search-filters parser.
 *
 * Async because location steps may call Go-Search and/or Google Places, and
 * because `aiFallback` may call an LLM.
 *
 * Pipeline steps are gated by {@link ParserPipelineFlags}: `DEFAULT_PARSER_FLAGS`
 * says what a step does and whether it runs by default, and a caller turns off
 * the ones it already covers itself.
 * - Step 1 `extractFilters`
 * - `relativeDate` / `sortExtraction` / `locationIdentification`
 * - `googlePlacePredictions` — Places Autocomplete on residual
 * - Step 2 `confidence`
 * - `aiFallback` — LLM infers additional filters, when residual is left over
 * - `generateTags` — LLM assigns closed-list lifestyle tags on that same call
 * - Future: fuzzy
 */
export async function parseNaturalLanguageFilters(
  query: string,
  options: ParseNaturalLanguageFiltersOptions = {}
): Promise<ParseNaturalLanguageFiltersResult | null> {
  const started = performance.now();
  const flags = resolveParserFlags(options.flags);

  if (typeof query !== 'string') {
    return null;
  }

  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return null;
  }

  debugParserInput(trimmed, flags);

  const normalizedQuery = normalizeQuery(trimmed);
  let filters: ParsedSearchFilters = {};
  let spans: MatchSpan[] = [];

  if (flags.extractFilters) {
    const extracted = runFilterExtractors(normalizedQuery, {
      flags,
      now: options.now,
    });
    filters = extracted.partial;
    spans = extracted.spans;
  }

  if (flags.locationIdentification) {
    const location = await extractLocation(normalizedQuery);
    Object.assign(filters, location.partial);
    spans.push(...location.spans);
  }

  if (flags.googlePlacePredictions) {
    const leftover = deriveResidualFromSpans(normalizedQuery, spans);
    if (leftover) {
      const fromGoogle = await extractGooglePlacesLocation({
        normalizedQuery,
        leftover,
        existing: filters,
      });
      Object.assign(filters, fromGoogle.partial);
      spans.push(...fromGoogle.spans);
    }
  }

  // Future flagged steps (no-ops until implemented):
  // flags.fuzzyMatch

  if (flags.confidence) {
    spans = applyConfidenceStep(spans);
  } else {
    spans = stripConfidence(spans);
  }

  let unparsed =
    Object.keys(filters).length === 0 ? null : deriveResidualFromSpans(normalizedQuery, spans);
  let tags: string[] | null = null;

  // The model is only worth its round trip when there is something left for it
  // to read: nothing understood at all, or words the extractors could not
  // account for. A query they covered completely is already the right answer,
  // and blocking it on an LLM call would delay every keystroke of an
  // interactive caller to re-derive what is already known. Tags ride along on
  // that call rather than justifying one, so a fully understood query returns
  // without them and the caller shows its own.
  const hasFilters = Object.keys(filters).length > 0;
  const needsLlm = flags.aiFallback && (!hasFilters || unparsed !== null);

  if (needsLlm) {
    const provider = options.aiProvider ?? createDefaultSmartSearchAiProvider();
    if (provider) {
      const llm = await inferFiltersWithLlm({
        query: trimmed,
        residual: unparsed ?? (hasFilters ? null : normalizedQuery),
        existing: filters,
        generateTags: flags.generateTags,
        now: options.now ?? new Date(),
        provider,
      });
      if (llm) {
        filters = mergeFiltersFillGaps(filters, llm.filters);
        unparsed = llm.unparsed;
        if (flags.generateTags) {
          tags = llm.tags ?? [];
        }
      }
    }
  }

  if (Object.keys(filters).length === 0 && (tags === null || tags.length === 0)) {
    return emitEmptyAndReturnNull({
      trimmed,
      normalizedQuery,
      started,
      flags,
      onMetrics: options.onMetrics,
    });
  }

  const parsedFilters = ParsedSearchFiltersSchema.parse(filters);
  const durationMs = performance.now() - started;

  const metrics = buildMetrics({
    query: trimmed,
    normalizedQuery,
    spans,
    residual: unparsed,
    filters: parsedFilters,
    durationMs,
    confidenceEnabled: flags.confidence,
    flags,
  });

  const result: ParseNaturalLanguageFiltersResult = {
    filters: parsedFilters,
    tags,
    spans,
    unparsed,
    metrics,
  };

  // Runtime boundary check only. Zod 4 infers `.nullable()` / null-union object
  // keys as optional, which does not match our required `T | null` contract.
  ParseNaturalLanguageFiltersResultSchema.parse(result);
  emitMetrics(result.metrics, options.onMetrics);
  debugParserOutput(result);
  return result;
}
